import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Fab,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { alpha } from '@mui/material/styles';
import UserDashboard from 'layouts/UserDashboard';
import Container from 'components/Container';
import {
  getTransactions,
  patchTransactionCategory,
  createTransaction,
  deleteTransaction,
} from './FinancialService';
import { TransactionResponse } from './types';

const C = {
  ink: '#17263d',
  muted: '#78869a',
  blue: '#1475d1',
  green: '#159b72',
  red: '#ff454d',
};

const EXPENSE_CATEGORIES = ['NEEDS', 'WANTS', 'SAVINGS'];
const INCOME_CATEGORIES = ['SALARY', 'INCOME_OTHER'];

const catLabel = (c: string) =>
  c === 'INCOME_OTHER'
    ? 'Income (other)'
    : c.charAt(0) + c.slice(1).toLowerCase();

const fmt = (n: number) =>
  new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(
    Math.abs(n),
  );

const EXPENSE_CAT_OPTIONS = [
  {
    value: 'NEEDS',
    label: 'Needs',
    subCats: [
      { value: 'HOUSING', label: 'Housing' },
      { value: 'UTILITIES', label: 'Utilities' },
      { value: 'GROCERIES', label: 'Groceries' },
      { value: 'TRANSPORT', label: 'Transport' },
      { value: 'HEALTHCARE', label: 'Healthcare' },
      { value: 'INSURANCE', label: 'Insurance' },
      { value: 'COUNCIL_TAX', label: 'Council Tax' },
      { value: 'CHILDCARE', label: 'Childcare' },
      { value: 'NEEDS_OTHER', label: 'Other' },
    ],
  },
  {
    value: 'WANTS',
    label: 'Wants',
    subCats: [
      { value: 'DINING', label: 'Dining' },
      { value: 'ENTERTAINMENT', label: 'Entertainment' },
      { value: 'SHOPPING', label: 'Shopping' },
      { value: 'SUBSCRIPTIONS', label: 'Subscriptions' },
      { value: 'PERSONAL_CARE', label: 'Personal Care' },
      { value: 'TRAVEL', label: 'Travel' },
      { value: 'WANTS_OTHER', label: 'Other' },
    ],
  },
  {
    value: 'SAVINGS',
    label: 'Savings',
    subCats: [
      { value: 'SAVINGS', label: 'Savings' },
      { value: 'INVESTMENTS', label: 'Investments' },
      { value: 'PENSION', label: 'Pension' },
      { value: 'EMERGENCY_FUND', label: 'Emergency Fund' },
      { value: 'SAVINGS_OTHER', label: 'Other' },
    ],
  },
];

const INCOME_CAT_OPTIONS = [
  { value: 'SALARY', label: 'Salary', subCats: [] },
  { value: 'INCOME_OTHER', label: 'Other Income', subCats: [] },
];

const todayISO = () => new Date().toISOString().slice(0, 10);

interface AddTransactionModalProps {
  open: boolean;
  onClose: () => void;
  onAdded: () => void;
}

const AddTransactionModal = ({
  open,
  onClose,
  onAdded,
}: AddTransactionModalProps) => {
  const [direction, setDirection] = useState<'DEBIT' | 'CREDIT'>('DEBIT');
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('NEEDS');
  const [subCategory, setSubCategory] = useState('');
  const [date, setDate] = useState(todayISO());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      setDirection('DEBIT');
      setAmount('');
      setDescription('');
      setCategory('NEEDS');
      setSubCategory('');
      setDate(todayISO());
      setError('');
    }
  }, [open]);

  const catOptions =
    direction === 'DEBIT' ? EXPENSE_CAT_OPTIONS : INCOME_CAT_OPTIONS;
  const selectedCat = catOptions.find((c) => c.value === category);

  const handleDirectionChange = (
    _: React.MouseEvent<HTMLElement>,
    val: 'DEBIT' | 'CREDIT',
  ) => {
    if (!val) return;
    setDirection(val);
    setCategory(val === 'DEBIT' ? 'NEEDS' : 'SALARY');
    setSubCategory('');
  };

  const handleSubmit = async () => {
    const amt = parseFloat(amount);
    if (!description.trim()) {
      setError('Description is required.');
      return;
    }
    if (!amt || amt <= 0) {
      setError('Enter a valid amount greater than 0.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      await createTransaction({
        amount: amt,
        direction,
        description: description.trim(),
        category,
        subCategory: subCategory || undefined,
        date,
      });
      onAdded();
      onClose();
    } catch {
      setError('Could not save transaction. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Add transaction</DialogTitle>
      <DialogContent
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          pt: '12px !important',
        }}
      >
        {error && <Alert severity="error">{error}</Alert>}
        <ToggleButtonGroup
          value={direction}
          exclusive
          onChange={handleDirectionChange}
          fullWidth
          size="small"
        >
          <ToggleButton value="DEBIT">Expense</ToggleButton>
          <ToggleButton value="CREDIT">Income</ToggleButton>
        </ToggleButtonGroup>
        <TextField
          label="Amount (£)"
          type="number"
          size="small"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          inputProps={{ min: 0, step: '0.01' }}
          fullWidth
        />
        <TextField
          label="Description"
          size="small"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          fullWidth
        />
        <FormControl size="small" fullWidth>
          <InputLabel>Category</InputLabel>
          <Select
            value={category}
            label="Category"
            onChange={(e) => {
              setCategory(e.target.value);
              setSubCategory('');
            }}
          >
            {catOptions.map((c) => (
              <MenuItem key={c.value} value={c.value}>
                {c.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {selectedCat && selectedCat.subCats.length > 0 && (
          <FormControl size="small" fullWidth>
            <InputLabel>Sub-category (optional)</InputLabel>
            <Select
              value={subCategory}
              label="Sub-category (optional)"
              onChange={(e) => setSubCategory(e.target.value)}
              displayEmpty
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {selectedCat.subCats.map((s) => (
                <MenuItem key={s.value} value={s.value}>
                  {s.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        <TextField
          label="Date"
          type="date"
          size="small"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          fullWidth
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
        <Button onClick={onClose} variant="outlined" color="inherit">
          Cancel
        </Button>
        <Button onClick={handleSubmit} variant="contained" disabled={saving}>
          {saving ? 'Saving…' : 'Add transaction'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ─── DeleteTransactionDialog ──────────────────────────────────────────────────

interface DeleteTransactionDialogProps {
  open: boolean;
  transaction: TransactionResponse | null;
  onClose: () => void;
  onDeleted: () => void;
}

const DeleteTransactionDialog = ({
  open,
  transaction,
  onClose,
  onDeleted,
}: DeleteTransactionDialogProps) => {
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState('');

  const handleDelete = async () => {
    if (!transaction) return;
    setDeleting(true);
    setError('');
    try {
      await deleteTransaction(transaction.id);
      onDeleted();
      onClose();
    } catch {
      setError('Could not delete transaction. Please try again.');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Delete transaction?</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <Typography>
          This will permanently remove{' '}
          <strong>{transaction?.vendor || transaction?.description}</strong>.
          This cannot be undone.
        </Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
        <Button onClick={onClose} variant="outlined" color="inherit">
          Cancel
        </Button>
        <Button
          onClick={handleDelete}
          variant="contained"
          color="error"
          disabled={deleting}
        >
          {deleting ? 'Deleting…' : 'Delete'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const CATEGORY_COLORS: Record<string, string> = {
  NEEDS: '#159b72',
  WANTS: '#EC4899',
  SAVINGS: '#3B82F6',
  SALARY: '#8B5CF6',
  INCOME_OTHER: '#8B5CF6',
};

const TransactionsPage = (): JSX.Element => {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [rows, setRows] = useState<TransactionResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [catFilter, setCatFilter] = useState('ALL');
  const [page, setPage] = useState(0);
  const [savingId, setSavingId] = useState<number | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<TransactionResponse | null>(
    null,
  );
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const PAGE_SIZE = 25;

  const toggleExpanded = (id: number) =>
    setExpandedId((prev) => (prev === id ? null : id));

  const monthLabel = new Date(year, month - 1).toLocaleString('en-GB', {
    month: 'long',
    year: 'numeric',
  });

  const prevMonth = () => {
    if (month === 1) {
      setMonth(12);
      setYear((y) => y - 1);
    } else setMonth((m) => m - 1);
    setPage(0);
  };
  const nextMonth = () => {
    const next = new Date(year, month);
    if (next <= now) {
      if (month === 12) {
        setMonth(1);
        setYear((y) => y + 1);
      } else setMonth((m) => m + 1);
      setPage(0);
    }
  };

  const load = useCallback(() => {
    setLoading(true);
    getTransactions(year, month)
      .then((res) => setRows(res.data))
      .finally(() => setLoading(false));
  }, [year, month]);

  useEffect(() => {
    load();
  }, [load]);

  const handleCategoryChange = useCallback(
    async (tx: TransactionResponse, newCat: string) => {
      if (newCat === tx.category) return;
      setSavingId(tx.id);
      try {
        const res = await patchTransactionCategory(tx.id, newCat);
        setRows((prev) =>
          prev.map((r) =>
            r.id === tx.id ? { ...r, category: res.data.category } : r,
          ),
        );
      } catch {
        // revert on error — state unchanged
      } finally {
        setSavingId(null);
      }
    },
    [],
  );

  const filtered = useMemo(() => {
    return rows.filter((r) => {
      const matchSearch =
        !search ||
        r.description.toLowerCase().includes(search.toLowerCase()) ||
        r.vendor.toLowerCase().includes(search.toLowerCase());
      const matchCat = catFilter === 'ALL' || r.category === catFilter;
      return matchSearch && matchCat && !r.isExcluded;
    });
  }, [rows, search, catFilter]);

  const paginated = filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);
  const categories = [
    'ALL',
    ...Array.from(new Set(rows.map((r) => r.category))),
  ];

  return (
    <UserDashboard>
      <Box
        sx={{
          background: `linear-gradient(175deg, ${alpha(C.blue, 0.05)} 0%, transparent 40%)`,
          minHeight: '100vh',
          py: 4,
        }}
      >
        <Container>
          {/* Header */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              mb: 3,
              flexWrap: 'wrap',
              gap: 2,
            }}
          >
            <Box>
              <Typography variant="h4" fontWeight={700} sx={{ color: C.ink }}>
                Transactions
              </Typography>
              <Typography variant="body2" sx={{ color: C.muted }}>
                View and categorise your spending
              </Typography>
            </Box>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                flexWrap: 'wrap',
              }}
            >
              {/* Month navigation */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                <IconButton
                  size="small"
                  onClick={prevMonth}
                  aria-label="Previous month"
                  sx={{ color: C.muted, '&:hover': { color: C.ink } }}
                >
                  <ChevronLeftIcon fontSize="small" />
                </IconButton>
                <Typography
                  variant="body2"
                  fontWeight={600}
                  sx={{ color: C.ink, minWidth: 130, textAlign: 'center' }}
                >
                  {monthLabel}
                </Typography>
                <IconButton
                  size="small"
                  onClick={nextMonth}
                  disabled={new Date(year, month) > now}
                  aria-label="Next month"
                  sx={{ color: C.muted, '&:hover': { color: C.ink } }}
                >
                  <ChevronRightIcon fontSize="small" />
                </IconButton>
              </Box>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setAddOpen(true)}
                sx={{ display: { xs: 'none', sm: 'flex' } }}
              >
                Add transaction
              </Button>
            </Box>
          </Box>

          {/* Filters */}
          <Card sx={{ mb: 2 }}>
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <TextField
                  size="small"
                  placeholder="Search description or vendor…"
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPage(0);
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ minWidth: 260 }}
                />
                <Select
                  size="small"
                  value={catFilter}
                  onChange={(e) => {
                    setCatFilter(e.target.value);
                    setPage(0);
                  }}
                  sx={{ minWidth: 140 }}
                >
                  {categories.map((c) => (
                    <MenuItem key={c} value={c}>
                      {c === 'ALL' ? 'All categories' : c}
                    </MenuItem>
                  ))}
                </Select>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ alignSelf: 'center' }}
                >
                  {filtered.length} transaction
                  {filtered.length !== 1 ? 's' : ''}
                </Typography>
              </Box>
            </CardContent>
          </Card>

          {/* Table */}
          <Card>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: alpha(C.blue, 0.04) }}>
                    <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>
                      Vendor / Description
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Category</TableCell>
                    <TableCell sx={{ fontWeight: 700, textAlign: 'right' }}>
                      Amount
                    </TableCell>
                    <TableCell sx={{ width: 40 }} />
                    <TableCell sx={{ width: 32 }} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    Array.from({ length: 8 }).map((_, i) => (
                      <TableRow key={i}>
                        <TableCell>
                          <Skeleton />
                        </TableCell>
                        <TableCell>
                          <Skeleton />
                        </TableCell>
                        <TableCell>
                          <Skeleton width={80} />
                        </TableCell>
                        <TableCell>
                          <Skeleton width={70} />
                        </TableCell>
                        <TableCell />
                        <TableCell />
                      </TableRow>
                    ))
                  ) : paginated.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={6}
                        sx={{ textAlign: 'center', py: 5 }}
                      >
                        {rows.length === 0 ? (
                          <Box>
                            <Typography color="text.secondary" gutterBottom>
                              No transactions in {monthLabel}.
                            </Typography>
                            <Button
                              size="small"
                              variant="outlined"
                              href="/user/bank-accounts"
                              sx={{ mt: 1 }}
                            >
                              Import transactions
                            </Button>
                          </Box>
                        ) : (
                          <Box>
                            <Typography color="text.secondary" gutterBottom>
                              No transactions match your filters.
                            </Typography>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => {
                                setSearch('');
                                setCatFilter('ALL');
                                setPage(0);
                              }}
                              sx={{ mt: 1 }}
                            >
                              Clear filters
                            </Button>
                          </Box>
                        )}
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginated.map((tx) => {
                      const isExpanded = expandedId === tx.id;
                      return (
                        <React.Fragment key={tx.id}>
                          <TableRow
                            hover
                            onClick={() => toggleExpanded(tx.id)}
                            sx={{ cursor: 'pointer' }}
                          >
                            <TableCell
                              sx={{
                                whiteSpace: 'nowrap',
                                color: 'text.secondary',
                                fontSize: '0.8rem',
                              }}
                            >
                              {tx.date}
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" fontWeight={500}>
                                {tx.vendor || tx.description}
                              </Typography>
                              {tx.vendor && tx.description !== tx.vendor && (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  {tx.description}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell
                              sx={{ minWidth: 140 }}
                              onClick={(e) => e.stopPropagation()}
                            >
                              {savingId === tx.id ? (
                                <CircularProgress size={16} />
                              ) : (
                                <Tooltip
                                  title="Click to change category"
                                  placement="top"
                                  enterDelay={600}
                                >
                                  <Select
                                    value={tx.category}
                                    size="small"
                                    variant="standard"
                                    disableUnderline
                                    onChange={(e) =>
                                      handleCategoryChange(tx, e.target.value)
                                    }
                                    renderValue={(val) => (
                                      <Chip
                                        label={catLabel(val as string)}
                                        size="small"
                                        sx={{
                                          height: 20,
                                          fontSize: '0.65rem',
                                          bgcolor: alpha(
                                            CATEGORY_COLORS[val as string] ??
                                              C.muted,
                                            0.1,
                                          ),
                                          color:
                                            CATEGORY_COLORS[val as string] ??
                                            C.muted,
                                          fontWeight: 600,
                                          cursor: 'pointer',
                                          pointerEvents: 'none',
                                        }}
                                      />
                                    )}
                                    sx={{ '& .MuiSelect-select': { p: 0 } }}
                                  >
                                    {(tx.direction === 'CREDIT' ||
                                    tx.amount >= 0
                                      ? INCOME_CATEGORIES
                                      : EXPENSE_CATEGORIES
                                    ).map((cat) => (
                                      <MenuItem
                                        key={cat}
                                        value={cat}
                                        sx={{ fontSize: '0.85rem' }}
                                      >
                                        {catLabel(cat)}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                </Tooltip>
                              )}
                            </TableCell>
                            <TableCell
                              sx={{
                                textAlign: 'right',
                                fontWeight: 600,
                                color: tx.amount < 0 ? C.red : C.green,
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {tx.amount < 0 ? '−' : '+'}
                              {fmt(tx.amount)}
                            </TableCell>
                            <TableCell
                              sx={{ p: 0.5 }}
                              onClick={(e) => e.stopPropagation()}
                            >
                              <Tooltip title="Delete">
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    setDeleteTarget(tx);
                                    setDeleteDialogOpen(true);
                                  }}
                                  sx={{
                                    color: C.muted,
                                    '&:hover': { color: C.red },
                                  }}
                                >
                                  <DeleteOutlineIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                            <TableCell sx={{ p: 0.5, width: 32 }}>
                              <IconButton
                                size="small"
                                aria-label={isExpanded ? 'Collapse' : 'Expand'}
                                sx={{ color: C.muted }}
                              >
                                {isExpanded ? (
                                  <KeyboardArrowUpIcon fontSize="small" />
                                ) : (
                                  <KeyboardArrowDownIcon fontSize="small" />
                                )}
                              </IconButton>
                            </TableCell>
                          </TableRow>

                          {/* Expandable detail row */}
                          <TableRow>
                            <TableCell colSpan={6} sx={{ p: 0, border: 0 }}>
                              <Collapse
                                in={isExpanded}
                                timeout="auto"
                                unmountOnExit
                              >
                                <Box
                                  sx={{
                                    px: 2.5,
                                    py: 1.5,
                                    bgcolor: alpha(C.blue, 0.03),
                                    borderBottom: `1px solid ${alpha(C.muted, 0.12)}`,
                                  }}
                                >
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      gap: 3,
                                      flexWrap: 'wrap',
                                      alignItems: 'flex-start',
                                    }}
                                  >
                                    {/* Direction */}
                                    <Box>
                                      <Typography
                                        variant="caption"
                                        sx={{
                                          color: C.muted,
                                          fontWeight: 700,
                                          textTransform: 'uppercase',
                                          letterSpacing: 0.4,
                                        }}
                                      >
                                        Type
                                      </Typography>
                                      <Typography
                                        variant="body2"
                                        sx={{ mt: 0.25 }}
                                      >
                                        {tx.direction === 'CREDIT'
                                          ? 'Income'
                                          : tx.direction === 'DEBIT'
                                            ? 'Expense'
                                            : '—'}
                                      </Typography>
                                    </Box>

                                    {/* Sub-category */}
                                    {tx.subCategory && (
                                      <Box>
                                        <Typography
                                          variant="caption"
                                          sx={{
                                            color: C.muted,
                                            fontWeight: 700,
                                            textTransform: 'uppercase',
                                            letterSpacing: 0.4,
                                          }}
                                        >
                                          Sub-category
                                        </Typography>
                                        <Typography
                                          variant="body2"
                                          sx={{ mt: 0.25 }}
                                        >
                                          {tx.subCategory.charAt(0) +
                                            tx.subCategory
                                              .slice(1)
                                              .toLowerCase()
                                              .replace(/_/g, ' ')}
                                        </Typography>
                                      </Box>
                                    )}

                                    {/* Note */}
                                    {tx.note && (
                                      <Box sx={{ flex: 1, minWidth: 160 }}>
                                        <Typography
                                          variant="caption"
                                          sx={{
                                            color: C.muted,
                                            fontWeight: 700,
                                            textTransform: 'uppercase',
                                            letterSpacing: 0.4,
                                          }}
                                        >
                                          Note
                                        </Typography>
                                        <Typography
                                          variant="body2"
                                          sx={{ mt: 0.25 }}
                                        >
                                          {tx.note}
                                        </Typography>
                                      </Box>
                                    )}

                                    {/* Flags */}
                                    <Box
                                      sx={{
                                        display: 'flex',
                                        gap: 0.75,
                                        flexWrap: 'wrap',
                                        alignItems: 'center',
                                        mt: 0.25,
                                      }}
                                    >
                                      {tx.isRecurring && (
                                        <Chip
                                          label="Recurring"
                                          size="small"
                                          sx={{
                                            height: 20,
                                            fontSize: '0.65rem',
                                            bgcolor: alpha(C.blue, 0.1),
                                            color: C.blue,
                                            fontWeight: 600,
                                          }}
                                        />
                                      )}
                                      {tx.isPersonal && (
                                        <Chip
                                          label="Personal"
                                          size="small"
                                          sx={{
                                            height: 20,
                                            fontSize: '0.65rem',
                                            bgcolor: alpha(C.muted, 0.1),
                                            color: C.muted,
                                            fontWeight: 600,
                                          }}
                                        />
                                      )}
                                      {tx.isExcluded && (
                                        <Chip
                                          label="Excluded from budget"
                                          size="small"
                                          sx={{
                                            height: 20,
                                            fontSize: '0.65rem',
                                            bgcolor: alpha(C.muted, 0.08),
                                            color: C.muted,
                                            fontWeight: 600,
                                          }}
                                        />
                                      )}
                                    </Box>
                                  </Box>
                                </Box>
                              </Collapse>
                            </TableCell>
                          </TableRow>
                        </React.Fragment>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Pagination */}
            {!loading && filtered.length > PAGE_SIZE && (
              <Box
                sx={{ display: 'flex', justifyContent: 'center', gap: 1, p: 2 }}
              >
                <Button
                  size="small"
                  disabled={page === 0}
                  onClick={() => setPage((p) => p - 1)}
                >
                  Previous
                </Button>
                <Typography variant="body2" sx={{ alignSelf: 'center' }}>
                  Page {page + 1} of {Math.ceil(filtered.length / PAGE_SIZE)}
                </Typography>
                <Button
                  size="small"
                  disabled={(page + 1) * PAGE_SIZE >= filtered.length}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Next
                </Button>
              </Box>
            )}
          </Card>
        </Container>
      </Box>

      {/* Floating Add button (mobile) */}
      <Fab
        color="primary"
        aria-label="add transaction"
        onClick={() => setAddOpen(true)}
        sx={{
          position: 'fixed',
          bottom: 32,
          right: 32,
          display: { sm: 'none' },
        }}
      >
        <AddIcon />
      </Fab>

      <AddTransactionModal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onAdded={load}
      />
      <DeleteTransactionDialog
        open={deleteDialogOpen}
        transaction={deleteTarget}
        onClose={() => setDeleteDialogOpen(false)}
        onDeleted={load}
      />
    </UserDashboard>
  );
};

export default TransactionsPage;
